// standards-ignore: prefer-sdk-enums — see subQuestionResponseType.ts: these
// options drive fields typed as the local `enum ResponseType` / `enum ShowOn`,
// so the values must be the local enums (SDK values are not nominally
// assignable to those fields).
import { ResponseType, ShowOn } from '../../../../types/helios/Question';
import type { OptionSelectItem } from '../../../shared/ui/forms/OptionSelect';
import type { SubKey } from './subQuestionsMessages';

/** A response-type / show-on choice: the enum value plus the i18n key for its
 *  visible label. The row resolves `labelKey` through `t` at render. */
export interface ResponseTypeChoice {
  readonly value: string;
  readonly labelKey: SubKey;
}

/** Every response type, in the manual-flow order (Pass/Fail first — the
 *  default). Data-collection types follow. */
export const RESPONSE_TYPE_CHOICES: readonly ResponseTypeChoice[] = [
  { value: ResponseType.PASS_FAIL, labelKey: 'subResponseTypePassFail' },
  { value: ResponseType.TEXT, labelKey: 'subResponseTypeText' },
  { value: ResponseType.NUMERIC, labelKey: 'subResponseTypeNumeric' },
  { value: ResponseType.SINGLE_SELECT, labelKey: 'subResponseTypeSingleSelect' },
  { value: ResponseType.MULTI_SELECT, labelKey: 'subResponseTypeMultiSelect' },
  { value: ResponseType.YES_NO, labelKey: 'subResponseTypeYesNo' },
  { value: ResponseType.USER_ALIAS, labelKey: 'subResponseTypeUserAlias' },
];

/** The three conditional-display triggers. */
export const SHOW_ON_CHOICES: readonly ResponseTypeChoice[] = [
  { value: ShowOn.PASS, labelKey: 'subShowOnPass' },
  { value: ShowOn.FAIL, labelKey: 'subShowOnFail' },
  { value: ShowOn.BOTH, labelKey: 'subShowOnBoth' },
];

/**
 * Resolves a choice list into `OptionSelectItem[]` by translating each
 * `labelKey`. Kept pure and out of the component so the mapped array can be
 * memoised at the call site for OptionSelect's reference-identity contract.
 */
export function toSelectItems(
  choices: readonly ResponseTypeChoice[],
  t: (key: SubKey) => string,
): OptionSelectItem[] {
  return choices.map((choice) => ({ value: choice.value, label: t(choice.labelKey) }));
}

/**
 * The i18n label key for a sub-question's response type (absent → Pass/Fail,
 * the default). Used by the row-header type badge. Falls back to the Pass/Fail
 * key for an unknown value so the badge always renders a real label.
 */
export function responseTypeLabelKey(value: string | undefined): SubKey {
  const match = RESPONSE_TYPE_CHOICES.find((choice) => choice.value === value);
  return match ? match.labelKey : 'subResponseTypePassFail';
}
